import type { ApiRequestBody, ApiResponseBody, ApiSearchQuery } from '@/lib/api/api-type-helper';

// ---------------------------------------------------------------------------
// List API Types
// ---------------------------------------------------------------------------

export type GetTicketListQuery = ApiSearchQuery<'/v2/pn-force/tickets', 'get'>;
export type GetTicketListResponse = ApiResponseBody<'/v2/pn-force/tickets', 'get'>;
export type TicketListItem = GetTicketListResponse['items'][0];
export type TicketOrderBy = NonNullable<GetTicketListQuery>['orderBy'];

// ---------------------------------------------------------------------------
// CSV Export API Types（簡易エクスポート: 一覧表示列を view モードで出力）
// ---------------------------------------------------------------------------

/** 簡易エクスポートのフィルタ。一覧 API と同じ絞り込みを使う（mode/encoding は固定注入）。 */
export type GetTicketsCsvFilters = GetTicketListQuery;
export type GetTicketsCsvResponse = ApiResponseBody<'/v2/pn-force/tickets/csv', 'get'>;

// ---------------------------------------------------------------------------
// Count API Types
// ---------------------------------------------------------------------------

export type GetTicketCountQuery = ApiSearchQuery<'/v2/pn-force/tickets/count', 'get'>;
export type GetTicketCountResponse = ApiResponseBody<'/v2/pn-force/tickets/count', 'get'>;

// ---------------------------------------------------------------------------
// Detail API Types
// ---------------------------------------------------------------------------

/**
 * チケットのプロダクトスコープ紐付け対象になりうる ProductNode の kind。
 * PRODUCT ルートは紐付け不可（BE バリデーションと一致）。
 * GET の `productNodeKind` は BE の enum 定義上 PRODUCT を含むが、
 * 紐付け候補（選択ダイアログ）はこの 3 値に限定する。
 */
export type TicketProductNodeKind = 'MODULE' | 'SUB_MODULE' | 'FUNCTION';
export const TicketProductNodeKind = {
  MODULE: 'MODULE',
  SUB_MODULE: 'SUB_MODULE',
  FUNCTION: 'FUNCTION'
} as const satisfies Record<string, TicketProductNodeKind>;

/**
 * プロダクトスコープは M:N（2026-07-12）。機能系ノードは `productNodes`
 * （`{ id, name, path, kind }` の配列。path は祖先名・上位→下位・PRODUCT ルートと
 * 自ノードを含まず）、仕様は `productSpecifications`
 * （`{ id, name, functionId, functionName }` の配列）で返る。いずれも未割当は空配列。
 */
export type GetTicketResponse = ApiResponseBody<'/v2/pn-force/tickets/{id}', 'get'>;

// ---------------------------------------------------------------------------
// Dev Document Link Types
// ---------------------------------------------------------------------------

/**
 * 開発チケットに紐づく開発ドキュメント（詳細レスポンスの `devDocuments` 要素）。
 * `{ id, code, title, kind, status }`。紐付けの書き込みは PATCH の
 * `devDocumentIds`（全置換）で行う（専用 add/remove エンドポイントは無い）。
 */
export type TicketDevDocumentItem = GetTicketResponse['devDocuments'][number];

// ---------------------------------------------------------------------------
// Issue Link Types
// ---------------------------------------------------------------------------

/**
 * 開発チケットに紐づく不具合（詳細レスポンスの `issues` 要素）。
 * `{ id, name, severity, status }`。「このチケットでどの不具合を直すのか」の
 * 直接紐付け（IssueTicket の N:M）由来のみで、プロダクト横断可（`code` は持たない）。
 * 紐付けの書き込みは PATCH の `issueIds`（全置換）で行う
 * （専用 add/remove エンドポイントは無い）。**同じ行集合は不具合側の PATCH
 * `ticketIds` からも全置換される**（双方向 write）ため、部分配列を送ると
 * 不具合側で作った紐付けが消える。間接経路（IssueTestSet → TestSetTicket）は
 * 併存させず、ここには直接紐付けだけが載る。
 */
export type TicketIssueItem = GetTicketResponse['issues'][number];

// ---------------------------------------------------------------------------
// Assignee Role Types
// ---------------------------------------------------------------------------

/** 担当者の役割（主担当 / レビュアー / オブザーバー）。未設定は null。 */
export type TicketAssigneeRole = NonNullable<GetTicketResponse['assignees'][number]['role']>;

/** 役割の表示・選択順（主担当 → レビュアー → オブザーバーの序列を維持）。 */
export const TICKET_ASSIGNEE_ROLES = ['PRIMARY', 'REVIEWER', 'OBSERVER'] as const satisfies readonly TicketAssigneeRole[];

// ---------------------------------------------------------------------------
// Patch API Types
// ---------------------------------------------------------------------------

/**
 * プロダクトスコープは `productNodeIds`（機能系ノード）+ `specificationIds`（仕様）の
 * 2 配列の replace-all 契約（配列指定で全置換・空配列で全解除 / undefined = 変更なし）。
 */
export type PatchTicketRequest = ApiRequestBody<'/v2/pn-force/tickets/{id}', 'patch'>;

/** PATCH レスポンスは詳細 GET と同 shape。 */
export type PatchTicketResponse = ApiResponseBody<'/v2/pn-force/tickets/{id}', 'patch'>;
export type PatchTicketStatusRequest = ApiRequestBody<'/v2/pn-force/tickets/{id}/status', 'patch'>;
export type PatchTicketStatusResponse = ApiResponseBody<'/v2/pn-force/tickets/{id}/status', 'patch'>;

// ---------------------------------------------------------------------------
// Post Create API Types
// ---------------------------------------------------------------------------

export type PostTicketRequest = ApiRequestBody<'/v2/pn-force/tickets', 'post'>;
export type PostTicketResponse = ApiResponseBody<'/v2/pn-force/tickets', 'post'>;

// ---------------------------------------------------------------------------
// Dev Scope (Production 詳細 > 開発スコープ編集) API Types
// ---------------------------------------------------------------------------

/** 開発スコープ追加候補 Ticket 一覧の query（productionId は path 由来で固定注入）。 */
export type GetAssignableTicketsQuery = ApiSearchQuery<'/v2/pn-force/productions/{id}/assignable-tickets', 'get'>;
export type GetAssignableTicketsResponse = ApiResponseBody<'/v2/pn-force/productions/{id}/assignable-tickets', 'get'>;
/** 開発スコープへ Ticket を一括追加する body。 */
export type PostDevScopeTicketsRequest = ApiRequestBody<'/v2/pn-force/productions/{id}/dev-scope-tickets', 'post'>;
export type PostDevScopeTicketsResponse = ApiResponseBody<'/v2/pn-force/productions/{id}/dev-scope-tickets', 'post'>;
export type DeleteDevScopeTicketResponse = ApiResponseBody<'/v2/pn-force/productions/{id}/dev-scope-tickets/{ticketId}', 'delete'>;

// ---------------------------------------------------------------------------
// Ticket Enums (BE 定義 8 / 6 / 4 / 4 値）
// ---------------------------------------------------------------------------

export const TicketKind = {
  NEW_FEATURE: 'NEW_FEATURE',
  ENHANCEMENT: 'ENHANCEMENT',
  BUG_FIX: 'BUG_FIX',
  REFACTORING: 'REFACTORING',
  TECH_DEBT: 'TECH_DEBT',
  RESEARCH: 'RESEARCH',
  OPS: 'OPS',
  OTHER: 'OTHER'
} as const;
export type TicketKind = (typeof TicketKind)[keyof typeof TicketKind];
const TICKET_KIND_VALUES: ReadonlySet<string> = new Set(Object.values(TicketKind));
export function isTicketKind(value: string | null | undefined): value is TicketKind {
  return value != null && TICKET_KIND_VALUES.has(value);
}
export const TicketStatus = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  IN_REVIEW: 'IN_REVIEW',
  DONE: 'DONE',
  CLOSED: 'CLOSED',
  ARCHIVED: 'ARCHIVED'
} as const;
export type TicketStatus = (typeof TicketStatus)[keyof typeof TicketStatus];
const TICKET_STATUS_VALUES: ReadonlySet<string> = new Set(Object.values(TicketStatus));
export function isTicketStatus(value: string | null | undefined): value is TicketStatus {
  return value != null && TICKET_STATUS_VALUES.has(value);
}

/** 一覧画面のステータスフィルタ用。ドメイン値 + "ALL"。 */
export type TicketStatusFilter = TicketStatus | 'ALL';
export const TicketPriority = {
  P0: 'P0',
  P1: 'P1',
  P2: 'P2',
  P3: 'P3'
} as const;
export type TicketPriority = (typeof TicketPriority)[keyof typeof TicketPriority];
const TICKET_PRIORITY_VALUES: ReadonlySet<string> = new Set(Object.values(TicketPriority));
export function isTicketPriority(value: string | null | undefined): value is TicketPriority {
  return value != null && TICKET_PRIORITY_VALUES.has(value);
}
export const TicketSeverity = {
  CRITICAL: 'CRITICAL',
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW'
} as const;
export type TicketSeverity = (typeof TicketSeverity)[keyof typeof TicketSeverity];
const TICKET_SEVERITY_VALUES: ReadonlySet<string> = new Set(Object.values(TicketSeverity));
export function isTicketSeverity(value: string | null | undefined): value is TicketSeverity {
  return value != null && TICKET_SEVERITY_VALUES.has(value);
}